// src/components/small-format/FinishingMaterialOptions.js
import React from 'react';

// Generic dropdown component for finishing material selection
const FinishingMaterialOptions = ({
  processId, // 'laminate', 'duplex-laminate', 'encapsulate', 'punch', etc.
  finishingOptions,
  handleOptionChange,
  finishingMaterials = {}, // Object with process IDs as keys and arrays of materials as values
  showLabel = false,
  className = ''
}) => {
  // Special handling for punch process which needs two dropdowns
  if (processId === 'punch') {
    // Get materials for punch process
    const processMaterials = finishingMaterials[processId] || [];

    // Filter materials by type
    const holeCountOptions = processMaterials.filter(m => m.material_type === 'numberOfHoles');
    const holeDiameterOptions = processMaterials.filter(m => m.material_type === 'holeDiameter');

    // Get selected values
    const selectedHoleCount = finishingOptions[processId]?.['numberOfHoles'] || '';
    const selectedHoleDiameter = finishingOptions[processId]?.['holeDiameter'] || '';

    // Fallback defaults if no materials data
    const defaultHoleCountOptions = holeCountOptions.length > 0 ? holeCountOptions : [
      { material_id: '1', name: '1' },
      { material_id: '2', name: '2' },
      { material_id: '3', name: '3' },
      { material_id: '4', name: '4' }
    ];

    const defaultHoleDiameterOptions = holeDiameterOptions.length > 0 ? holeDiameterOptions : [
      { material_id: '3mm', name: '3mm' },
      { material_id: '6mm', name: '6mm' },
      { material_id: '8mm', name: '8mm' }
    ];

    return (
      <div className={`inline-flex items-center space-x-2 ${className}`}>
        <select
          value={selectedHoleCount}
          onChange={(e) => handleOptionChange(processId, 'numberOfHoles', e.target.value)}
          className="text-xs border rounded py-0.5 px-1.5 w-16"
          aria-label="Number of holes"
        >
          <option value="">Holes</option>
          {defaultHoleCountOptions.map(material => (
            <option key={material.material_id} value={material.material_id}>
              {material.name}
            </option>
          ))}
        </select>

        <select
          value={selectedHoleDiameter}
          onChange={(e) => handleOptionChange(processId, 'holeDiameter', e.target.value)}
          className="text-xs border rounded py-0.5 px-1.5 w-16"
          aria-label="Hole width"
        >
          <option value="">Width</option>
          {defaultHoleDiameterOptions.map(material => (
            <option key={material.material_id} value={material.material_id}>
              {material.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Original logic for other processes (laminate, encapsulate, etc.)
  // Material option key (maintained for backward compatibility with laminate_type)
  const optionKey = processId === 'laminate' || processId === 'duplex-laminate'
    ? 'laminateType'
    : 'material_type';

  // Get the selected value from options
  const selectedValue = finishingOptions[processId]?.[optionKey] || '';

  // Get materials for this process from the finishingMaterials object
  const processMaterials = finishingMaterials[processId] || [];

  // Fallback default options if no materials data is available
  const getDefaultOptions = () => {
    switch (processId) {
      case 'laminate':
      case 'duplex-laminate':
        return [
          { material_id: 'matt_lam', name: 'Matt' },
          { material_id: 'gloss_lam', name: 'Gloss' },
          { material_id: 'soft_touch_lam', name: 'Soft Touch' }
        ];
      case 'encapsulate':
        return [
          { material_id: '250matt', name: 'Matt (250 mic)' },
          { material_id: '250gloss', name: 'Gloss (250 mic)' }
        ];
      default:
        return [];
    }
  };

  // Use process materials if available, otherwise fallback to defaults
  const materialOptions = processMaterials.length > 0 ? processMaterials : getDefaultOptions();

  // If no material options are available, don't render anything
  if (materialOptions.length === 0) return null;

  return (
    <div className={`inline-flex items-center space-x-2 ${className}`}>
      {showLabel && (
        <label className="text-xs font-medium">Type:</label>
      )}
      <select
        value={selectedValue}
        onChange={(e) => handleOptionChange(processId, optionKey, e.target.value)}
        className="text-xs border rounded py-0.5 px-1.5" // Smaller styling to fit inline
        aria-label={`${processId} type`}
      >
        <option value="">Select...</option>
        {materialOptions.map(material => (
          <option key={material.material_id} value={material.material_id}>
            {material.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default FinishingMaterialOptions;